import {Injectable} from '@angular/core'

import {Observable} from 'rxjs'
import {first, map, timeout} from 'rxjs/operators'

import {NgxPermissionsService} from 'ngx-permissions'

import {Role, UserService} from '@app/core/services/rest-api'

import {LoggerService} from '@app/core/services/logger.service'
import {ContextService} from '@app/core/services/context.service'

import {
  SYSTEM_ADMINISTRATOR_PERMISSIONS,
  ADMINISTRATOR_PERMISSIONS,
  CONFIGURATION_MANAGER_PERMISSIONS,
  CONFIGURATION_OPERATOR_PERMISSIONS,
  BASIC_CONFIGURATION_OPERATOR_PERMISSIONS,
  CONFIGURATION_TEMPLATE_ONLY_PERMISSIONS,
  REPORTING_OPERATOR_PERMISSIONS,
  CONTRACT_MANAGER_PERMISSIONS,
  CONTRACT_OPERATOR_PERMISSIONS,
  DISTRIBUTION_MANAGER_PERMISSIONS,
  PAYTER_INTERNAL_PERMISSIONS,
  DIAGNOSTICS_OPERATOR_PERMISSIONS,
  VIEW_ONLY_PERMISSIONS,
} from './permissions'

const ROLE_MAP = {
  'system administrator': SYSTEM_ADMINISTRATOR_PERMISSIONS,
  administrator: ADMINISTRATOR_PERMISSIONS,
  'configuration manager': CONFIGURATION_MANAGER_PERMISSIONS,
  'configuration operator': CONFIGURATION_OPERATOR_PERMISSIONS,
  'basic configuration operator': BASIC_CONFIGURATION_OPERATOR_PERMISSIONS,
  'configuration template-only operator':
    CONFIGURATION_TEMPLATE_ONLY_PERMISSIONS,
  'reporting operator': REPORTING_OPERATOR_PERMISSIONS,
  'contract manager': CONTRACT_MANAGER_PERMISSIONS,
  'contract operator': CONTRACT_OPERATOR_PERMISSIONS,
  'distribution manager': DISTRIBUTION_MANAGER_PERMISSIONS,
  'payter internal': PAYTER_INTERNAL_PERMISSIONS,
  'diagnostics operator': DIAGNOSTICS_OPERATOR_PERMISSIONS,
  'view only': VIEW_ONLY_PERMISSIONS,
}

@Injectable()
export class AccessControlService {
  constructor(
    private userSvc: UserService,
    private ngxPermission: NgxPermissionsService,
    private context: ContextService,
    private logger: LoggerService,
  ) {}

  async loadPermissions() {
    // this async function waits for the request to complete
    // timeout after 2 seconds
    await this.loadPermissionsAsync().pipe(timeout(2000)).toPromise()
  }

  loadPermissionsAsync(): Observable<void> {
    return this.getRoles().pipe(
      first(),
      map((roles) => {
        // translate roles into a single list of ngx-permissions
        const permissions: string[] = roles
          .map((role: Role) => {
            // use the role name as key to the permission map
            const key = role.name.toLowerCase()
            if (ROLE_MAP[key] === undefined) {
              return []
            }
            return ROLE_MAP[key]
          })
          .reduce((previous: string[], permissions: string[]) => {
            // concat the permissions
            return previous.concat(
              previous,
              //filter duplicate items
              permissions.filter((item) => previous.indexOf(item) < 0),
            )
          }, [])

        // load the permissions into the ngx service
        this.ngxPermission.loadPermissions(permissions)
      }),
    )
  }

  flushPermissions() {
    this.ngxPermission.flushPermissions()
  }

  private getRoles(): Observable<Role[]> {
    return this.userSvc.getUserRoles(
      this.context.loginDomain,
      this.context.userId,
    )
  }
}
